import React, { PropsWithChildren, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Avatar,
  Button,
  Box,
  Divider,
  IconButton,
  Menu,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";

import { AuthContext } from "../../context/auth-context";
import { useGetClientWithIds } from "../../store";
import { User, UserType } from "../../types";
import { userHasPermission } from "../../types-helpers";
import RoleChip, { RoleChipValue } from "../chips/role-chip";

import { RouteType } from "../../router";
import ChangeClientModal from "./change-client-modal";
import { ClientAvatar, UserAvatar } from "../avatars";

interface AccountDropdownProps {
  currentUser?: User | null;
}

export const AccountDropdown: React.FC<
  PropsWithChildren<AccountDropdownProps>
> = ({ currentUser, children }) => {
  const { logout, clientId, availableClientIds, switchClientId } =
    useContext(AuthContext);
  const { data: clients } = useGetClientWithIds(availableClientIds);
  const [isChangeClientModalOpen, setIsChangeClientModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(clientId);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const profileName = currentUser?.fullName || "Guest";
  const initials =
    profileName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "G";
  const isMenuOpen = Boolean(anchorEl);
  const activeClientName = clients?.find(
    (client) => client?.clientId === clientId,
  )?.clientName;
  const canSwapClients = availableClientIds.length > 1;
  // TODO separate client and dev admin so that dev admin doesnt see hover effect for client settings.
  const isClientAdmin = userHasPermission(UserType.SysAdmin, currentUser);
  const isDeveloperAdmin = userHasPermission(
    UserType.DeveloperAdmin,
    currentUser,
  );
  const adminRole = isDeveloperAdmin
    ? "Developer Admin"
    : isClientAdmin
      ? "Client Admin"
      : undefined;
  const hasClientHoverActions = canSwapClients || isClientAdmin;
  const clientDisplayName = activeClientName || "No client assigned";

  const onClickLogout = () => {
    logout();
    navigate("/");
  };

  const openChangeClientModal = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    setSelectedClientId(clientId);
    setIsChangeClientModalOpen(true);
  };

  const onOpenClientSettings = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    navigate(RouteType.Client);
  };

  const closeChangeClientModal = () => {
    setIsChangeClientModalOpen(false);
  };

  const onChangeSelectedClient = (event: SelectChangeEvent) => {
    setSelectedClientId(event.target.value as string);
  };

  const onApplyClientChange = () => {
    const result = switchClientId(selectedClientId);

    if (result) {
      toast.success("Changed Client");
      setIsChangeClientModalOpen(false);
      return;
    }

    toast.error("Failed to change client");
  };

  const onOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={profileName}>
        <IconButton
          onClick={onOpenMenu}
          size="small"
          aria-controls={isMenuOpen ? "calendar-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? "true" : undefined}
        >
          <Avatar
            sx={{
              backgroundColor: "#055480",
              color: "primary.contrastText",
              width: 30,
              height: 30,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            <Typography variant="body2">{initials}</Typography>
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="calendar-menu"
        open={isMenuOpen}
        onClose={onCloseMenu}
        onClick={onCloseMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              minWidth: "320px",
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
          list: {
            sx: {
              py: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Stack>
          <Box
            onClick={() => navigate(RouteType.Account)}
            sx={{
              px: 1.5,
              py: 1.5,
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              minHeight: 40,
              overflow: "hidden",
              "&:hover .profile-default-content": {
                opacity: 0,
              },
              "&:hover .profile-hover-overlay": {
                opacity: 1,
              },
            }}
          >
            <Box
              className="profile-default-content"
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                opacity: 1,
                transition: "opacity 0.15s ease-in-out",
              }}
            >
              <UserAvatar text={profileName} color="#055480" />
              <Stack sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "1rem" }}
                  noWrap
                >
                  {profileName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.9rem", lineHeight: 1 }}
                  noWrap
                >
                  {currentUser?.email || ""}
                </Typography>
              </Stack>
            </Box>
            <Box
              className="profile-hover-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                backgroundColor: "action.hover",
                opacity: 0,
                transition: "opacity 0.15s ease-in-out",
              }}
            >
              <SettingsIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.9rem", lineHeight: 1 }}
                noWrap
              >
                Click to change account settings
              </Typography>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                textAlign: "center",
                fontSize: "0.75rem",
                lineHeight: 1,
                mb: 0.75,
              }}
            >
              Using as part of:
            </Typography>
            <Box
              onClick={(event) => {
                event.stopPropagation();

                if (isClientAdmin) {
                  onOpenClientSettings(event);
                  return;
                }

                if (canSwapClients) {
                  openChangeClientModal(event);
                }
              }}
              sx={{
                position: "relative",
                px: 1,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                backgroundColor: hasClientHoverActions
                  ? "background.paper"
                  : "action.hover",
                cursor: hasClientHoverActions ? "pointer" : "default",
                "&:hover .client-default-content": {
                  opacity: hasClientHoverActions ? 0 : 1,
                },
                "&:hover .client-hover-overlay": {
                  opacity: hasClientHoverActions ? 1 : 0,
                },
              }}
            >
              <Box
                className="client-default-content"
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minWidth: 0,
                  opacity: 1,
                  transition: "opacity 0.15s ease-in-out",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <ClientAvatar text={clientDisplayName} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      minWidth: 0,
                    }}
                    noWrap
                  >
                    {clientDisplayName}
                  </Typography>
                </Box>
                {adminRole && (
                  <RoleChip
                    role={adminRole as RoleChipValue}
                    label={adminRole}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: "999px",
                      "& .MuiChip-label": {
                        fontSize: "0.7rem",
                        px: 0.75,
                        fontWeight: 700,
                      },
                    }}
                  />
                )}
              </Box>
              {hasClientHoverActions && (
                <Box
                  className="client-hover-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "stretch",
                    backgroundColor: "action.hover",
                    opacity: 0,
                    transition: "opacity 0.15s ease-in-out",
                  }}
                >
                  {canSwapClients && isClientAdmin ? (
                    <>
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                          openChangeClientModal(event);
                        }}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          borderRight: "1px solid",
                          borderColor: "divider",
                          cursor: "pointer",
                          color: "text.secondary",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        }}
                      >
                        <SwapHorizIcon fontSize="small" color="action" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.85rem", lineHeight: 1 }}
                        >
                          Swap
                        </Typography>
                      </Box>
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenClientSettings(event);
                        }}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          cursor: "pointer",
                          color: "text.secondary",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        }}
                      >
                        <SettingsIcon fontSize="small" color="action" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.85rem", lineHeight: 1 }}
                        >
                          Settings
                        </Typography>
                      </Box>
                    </>
                  ) : canSwapClients ? (
                    <Box
                      onClick={(event) => {
                        event.stopPropagation();
                        openChangeClientModal(event);
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        cursor: "pointer",
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      <SwapHorizIcon fontSize="small" color="action" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem", lineHeight: 1 }}
                      >
                        Swap
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenClientSettings(event);
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        cursor: "pointer",
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      <SettingsIcon fontSize="small" color="action" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem", lineHeight: 1 }}
                      >
                        Settings
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
        <Divider />
        {children}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {isDeveloperAdmin && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<AdminPanelSettingsIcon />}
              onClick={() => navigate(RouteType.Admin)}
              sx={{
                textTransform: "none",
                background: "#2b2b2b",
                color: "#ffffff",
                boxShadow: "none",
                fontWeight: 600,
                "&:hover": {
                  background: "rgb(92, 92, 92)",
                  boxShadow: "none",
                },
              }}
            >
              Admin Dashboard
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<QuestionMarkIcon />}
            onClick={() => navigate(RouteType.Help)}
            sx={{
              textTransform: "none",
              background: "#4fa570",
              color: "#ffffff",
              boxShadow: "none",
              "&:hover": {
                background: "#366b4a",
                boxShadow: "none",
              },
            }}
          >
            Help
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<LogoutIcon />}
            onClick={onClickLogout}
            sx={{
              textTransform: "none",
              background: "#ce2d2d",
              color: "#ffffff",
              boxShadow: "none",
              "&:hover": {
                background: "#8d2929",
                boxShadow: "none",
              },
            }}
          >
            Sign Out
          </Button>
          </Box>
        </Box>
      </Menu>
      <ChangeClientModal
        open={isChangeClientModalOpen}
        clients={clients}
        selectedClientId={selectedClientId}
        onClose={closeChangeClientModal}
        onChangeSelectedClient={onChangeSelectedClient}
        onApply={onApplyClientChange}
      />
    </>
  );
};

export default AccountDropdown;
